import React, { useState } from "react";
import { Modal, Upload, Button, message, Typography, Select, Divider, Table, Collapse } from "antd";
import { UploadOutlined, FileExcelOutlined, FileTextOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { DocumentType, DocumentStatus } from "~/utils/constants";
import { useCreateFile } from "~/services/file/file.mutations";
import { useGetClients } from "~/services/client/client.queries";
import { ClientStatus } from "~/utils/constants";

const { Text, Title } = Typography;

const CSV_COLUMNS = [
  { key: "TITLE", required: true, description: "Name/title of the artwork or item", example: "Untitled (Blue) 1/2" },
  { key: "ITEM_TYPE", required: true, description: "PIECE or PACKAGE", example: "PIECE" },
  { key: "ARTIST_NAME", required: false, description: "Name of the artist", example: "Jean-Michel Basquiat" },
  { key: "MEDIUM", required: false, description: "Material or medium of the work", example: "Oil on canvas" },
  { key: "LENGTH", required: false, description: "Length in inches", example: "48" },
  { key: "WIDTH", required: false, description: "Width in inches", example: "36" },
  { key: "HEIGHT", required: false, description: "Height in inches", example: "3" },
  { key: "WEIGHT", required: false, description: "Weight in lbs", example: "25" },
  { key: "VALUE", required: false, description: "Declared value in dollars (number only)", example: "50000" },
  { key: "STATUS", required: false, description: "NO_POSSESSION, AVAILABLE_LIVE, AVAILABLE_ON_HAND, RELEASED", example: "AVAILABLE_ON_HAND" },
  { key: "PACKING_TYPE", required: false, description: "For PIECE items only. LOOSE, SLIPCASE, CB_FOLD, SHADOW_BOX, FLOATING_SHADOW_BOX, POLY_ONLY, TRAVEL_FRAME, CRATE, SLATCRATE, PALLET", example: "CRATE" },
  { key: "CONTAINER_TYPE", required: false, description: "For PACKAGE items only. PACKAGE, T_Frame, SLIPCASE, BOX, CUSTOM, CRATE, SLATCRATE, CBIN, PALLET", example: "CRATE" },
  { key: "CLIENT_INTERNAL_REF", required: false, description: "Client's own reference number for this item", example: "JMB-2024-001" },
  { key: "NOTES", required: false, description: "Additional notes", example: "Handle with care" },
];

const AI_PROMPT = `I need you to convert my inventory/artwork data into a CSV file with these exact column headers. Output ONLY the CSV content, nothing else.

COLUMNS (in this exact order):
TITLE, ITEM_TYPE, ARTIST_NAME, MEDIUM, LENGTH, WIDTH, HEIGHT, WEIGHT, VALUE, STATUS, PACKING_TYPE, CONTAINER_TYPE, CLIENT_INTERNAL_REF, NOTES

CRITICAL: Every single physical object must be its own row. If a piece is a diptych, that is 2 separate rows — title them like "Untitled (Blue) 1/2" and "Untitled (Blue) 2/2". A triptych is 3 rows (1/3, 2/3, 3/3), and so on. Never combine multiple objects into one row.

RULES:
- REQUIRED fields: TITLE, ITEM_TYPE
- ITEM_TYPE must be one of: PIECE, PACKAGE (use PIECE for individual artworks, PACKAGE for crates/boxes containing items)
- STATUS must be one of: NO_POSSESSION, AVAILABLE_LIVE, AVAILABLE_ON_HAND, RELEASED
- PACKING_TYPE is for PIECE items only. Must be one of: LOOSE, SLIPCASE, CB_FOLD, SHADOW_BOX, FLOATING_SHADOW_BOX, POLY_ONLY, TRAVEL_FRAME, CRATE, SLATCRATE, PALLET
- CONTAINER_TYPE is for PACKAGE items only. Must be one of: PACKAGE, T_Frame, SLIPCASE, BOX, CUSTOM, CRATE, SLATCRATE, CBIN, PALLET
- Dimensions (LENGTH, WIDTH, HEIGHT) are in inches. WEIGHT is in lbs. VALUE is in dollars (number only, no $ sign).
- Leave optional fields blank if no data is available.
- Wrap any field containing commas in double quotes.
- Do not add any extra columns.

Here is my data to convert:
[PASTE YOUR DATA HERE]`;

const tableColumns = [
  {
    title: "Column Header",
    dataIndex: "key",
    key: "key",
    render: (text: string, record: (typeof CSV_COLUMNS)[0]) => (
      <Text strong={record.required} code>{text}</Text>
    ),
  },
  {
    title: "Required",
    dataIndex: "required",
    key: "required",
    width: 80,
    render: (val: boolean) => val ? <Text type="danger">Yes</Text> : <Text type="secondary">No</Text>,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
  },
  {
    title: "Example",
    dataIndex: "example",
    key: "example",
    render: (text: string) => <Text type="secondary">{text}</Text>,
  },
];

export const ImportInventoryModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);
  const createFileMutation = useCreateFile();

  const { data: clientsResponse, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });
  const clients = clientsResponse?.items || [];

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT);
      setCopied(true);
      message.success("AI prompt copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error("Failed to copy. Please try manually.");
    }
  };

  const handleUploadChange = (info: any) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1);

    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      if (file) {
        const isValidType =
          file.type === "text/csv" ||
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".csv") ||
          file.name.toLowerCase().endsWith(".pdf");

        if (!isValidType) {
          message.error("Please upload a CSV or PDF file");
          newFileList = [];
        } else if (file.size > 10 * 1024 * 1024) {
          message.error("File size must be less than 10MB");
          newFileList = [];
        }
      }
    }

    setFileList(newFileList);
  };

  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV or PDF file to upload");
      return;
    }

    if (!selectedClientId) {
      message.error("Please select a client");
      return;
    }

    setIsUploading(true);

    try {
      const file = fileList[0].originFileObj;
      if (!file) throw new Error("File not found");

      const isPDF = file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
      const fileTypeLabel = isPDF ? "PDF" : "CSV";

      const fileData = {
        file: file,
        title: `Inventory Import - ${file.name}`,
        type: DocumentType.INVENTORY,
        status: DocumentStatus.SENT,
        clientId: selectedClientId,
        visibleToStaff: true,
        visibleToClient: false,
      };

      await createFileMutation.mutateAsync(fileData);

      message.success(`${fileTypeLabel} file uploaded successfully. It will appear in the Pending Imports list for review.`);
      setFileList([]);
      setSelectedClientId(undefined);
      setOpen(false);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      message.error(error.response?.data?.message || "Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setSelectedClientId(undefined);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        disabled={isUploading}
        className="flex items-center h-11 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm space-x-2 text-base"
      >
        <UploadOutlined />
        <span>Import Inventory</span>
      </Button>
      <Modal
        open={open}
        title="Upload File for Import"
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={isUploading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={isUploading}
            disabled={fileList.length === 0 || !selectedClientId}
          >
            Upload for Review
          </Button>,
        ]}
        width={900}
      >
        <div className="space-y-4">
          {/* AI Prompt Helper */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <Title level={5} className="!mt-0 !mb-1">
              Have inventory data in another format?
            </Title>
            <Text type="secondary" className="text-xs">
              Copy this prompt, paste it into ChatGPT or any AI tool along with your data (spreadsheet, PDF, list, etc.),
              and it will convert it into the exact CSV format needed.
            </Text>
            <div className="mt-3">
              <Button
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={handleCopyPrompt}
                className={copied ? "border-green-500 text-green-600" : "border-gray-300 text-gray-700 hover:border-gray-400"}
              >
                {copied ? "Copied!" : "Copy AI Conversion Prompt"}
              </Button>
            </div>
          </div>

          {/* Column Reference - Collapsible */}
          <Collapse
            size="small"
            items={[
              {
                key: "columns",
                label: <Text strong>View CSV Column Reference</Text>,
                children: (
                  <div>
                    <Text type="secondary" className="block mb-3">
                      Your CSV must use these exact column headers. Only <Text strong>TITLE</Text> and <Text strong>ITEM_TYPE</Text> are required. Each physical object gets its own row.
                    </Text>
                    <Table
                      columns={tableColumns}
                      dataSource={CSV_COLUMNS}
                      rowKey="key"
                      size="small"
                      pagination={false}
                      scroll={{ y: 300 }}
                    />
                  </div>
                ),
              },
            ]}
          />

          <Divider className="!my-3" />

          {/* Client Selection */}
          <div>
            <Text strong className="text-sm mb-2 block">
              Select Client <span className="text-red-500">*</span>
            </Text>
            <Select
              className="w-full"
              placeholder="Search and select a client..."
              value={selectedClientId}
              onChange={setSelectedClientId}
              loading={clientsLoading}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={clients.map((client) => ({
                value: client.id,
                label:
                  client.companyName ||
                  [client.firstName, client.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  "Unknown Client",
              }))}
            />
          </div>

          {/* File Upload */}
          <div>
            <Text strong className="text-sm uppercase tracking-wide mb-2 block">
              Select File
            </Text>
            <Upload
              accept=".csv,.pdf"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false}
              maxCount={1}
              disabled={isUploading}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={isUploading}
              >
                Choose File (CSV or PDF)
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                {fileList[0].name?.toLowerCase().endsWith(".pdf") ? (
                  <FileTextOutlined className="text-red-500" />
                ) : (
                  <FileExcelOutlined className="text-green-500" />
                )}
                <Text>{fileList[0].name}</Text>
              </div>
            )}
            <Text type="secondary" className="text-xs mt-1 block">
              Accepted formats: CSV or PDF. Maximum file size: 10MB. File will appear in Pending Imports for review.
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};
